import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import NotificationService from '../../services/notificationService';
import { getImageURL } from '../../utils/api';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import ScheduleConflictModal from '../../components/ScheduleConflictModal';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  AreaChart,
  Area,
} from 'recharts';

// Ticket Health chart: bars are declared bottom-to-top for Recharts'
// stacking, so this fixes the Legend/Tooltip reading order back to the
// intuitive Open -> In Progress -> Resolved -> Closed regardless of that
// declaration order.
const TICKET_STATUS_ORDER = { open: 0, inProgress: 1, resolved: 2, closed: 3 };
const TICKET_STATUS_LEGEND = [
  { value: 'Open', color: '#f59e0b' },
  { value: 'In Progress', color: '#0ea5e9' },
  { value: 'Resolved', color: '#10b981' },
  { value: 'Closed', color: '#94a3b8' },
];

// Recharts' built-in <Legend payload={...}> doesn't reliably keep this exact
// array order across versions, so render it ourselves -- guarantees Open,
// In Progress, Resolved, Closed left-to-right no matter what.
function TicketStatusLegend() {
  return (
    <ul className="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
      {TICKET_STATUS_LEGEND.map((item) => (
        <li key={item.value} className="flex items-center gap-2 text-sm text-gray-600">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
          {item.value}
        </li>
      ))}
    </ul>
  );
}

// Category Breakdown exports: per-student ticket detail (not just counts),
// pulled from the same /tickets/report endpoint the Tickets Report page
// uses -- it already returns exactly this shape, scoped the same way
// (coordinated courses only), so no new backend endpoint was needed.
const TICKET_STATUS_TEXT = { open: 'Open', 'in-progress': 'In Progress', resolved: 'Resolved', closed: 'Closed' };

function fmtDateTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

const TICKET_DETAIL_HEADERS = [
  'Ticket ID', 'Student Name', 'Student ID', 'Registration No', 'Batch', 'Course',
  'Title', 'Status', 'Priority', 'Created At', 'Resolved At', 'Resolved By',
];

function ticketDetailRow(t) {
  return [
    t.ticketId,
    t.studentName || '',
    t.studentId ?? '',
    t.registrationNumber || '',
    t.batchName || '',
    t.courseName || '',
    t.title,
    TICKET_STATUS_TEXT[t.status] || t.status,
    t.priority,
    fmtDateTime(t.createdAt),
    fmtDateTime(t.resolvedAt),
    t.resolvedByName || '',
  ];
}

function ticketDetailWorksheet(tickets) {
  const aoa = [
    TICKET_DETAIL_HEADERS,
    ...(tickets.length ? tickets.map(ticketDetailRow) : [['No tickets in this category yet', '', '', '', '', '', '', '', '', '', '', '']]),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = TICKET_DETAIL_HEADERS.map(() => ({ wch: 18 }));
  return worksheet;
}

// Excel sheet names: max 31 chars, and can't contain \ / * ? : [ ]
function sanitizeSheetName(name, fallback = 'Course') {
  const cleaned = String(name || fallback)
    .replace(/[\\/*?:[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (cleaned || fallback).slice(0, 31);
}

function sanitizeFileNamePart(name, fallback = 'course') {
  const cleaned = String(name || fallback).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  return cleaned || fallback;
}

export default function Dashboard() {
  const [statsLoading, setStatsLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [distributionLoading, setDistributionLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [attendanceError, setAttendanceError] = useState(null);
  const [distributionError, setDistributionError] = useState(null);
  const [stats, setStats] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allCoursesAttendanceData, setAllCoursesAttendanceData] = useState([]);
  const [courseAttendanceData, setCourseAttendanceData] = useState([]);
  const [batchCountData, setBatchCountData] = useState([]);
  const [bengCountData, setBengCountData] = useState([]);
  const [bengCncsCountData, setBengCncsCountData] = useState([]);
  const [l3CountData, setL3CountData] = useState([]);
  const [citCountData, setCitCountData] = useState([]);
  const [englishDiplomaCountData, setEnglishDiplomaCountData] = useState([]);
  const [hndBusinessCountData, setHndBusinessCountData] = useState([]);
  const [baBusinessAdminCountData, setBaBusinessAdminCountData] = useState([]);
  const [l7SmlCountData, setL7SmlCountData] = useState([]);
  const [hndDigitalTechCountData, setHndDigitalTechCountData] = useState([]);

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedCourse, setSelectedCourse] = useState('hnd');
  const [selectedAttendanceCourses, setSelectedAttendanceCourses] = useState(['all']);
  const [isCourseFilterModalOpen, setIsCourseFilterModalOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');

  const [ticketSummary, setTicketSummary] = useState([]);
  const [ticketSummaryLoading, setTicketSummaryLoading] = useState(true);
  const [ticketSummaryError, setTicketSummaryError] = useState(null);
  const [selectedTicketCourse, setSelectedTicketCourse] = useState(null);
  const [ticketCategoryDetail, setTicketCategoryDetail] = useState([]);
  const [ticketCategoryLoading, setTicketCategoryLoading] = useState(false);
  const [selectedTicketCourseIds, setSelectedTicketCourseIds] = useState(['all']);
  const [isTicketCourseFilterModalOpen, setIsTicketCourseFilterModalOpen] = useState(false);
  const [allCourseTicketsDownloading, setAllCourseTicketsDownloading] = useState(false);
  const [categoryDownloadingId, setCategoryDownloadingId] = useState(null);
  const [ticketCourseSearchQuery, setTicketCourseSearchQuery] = useState('');

  const [courseTicketSummary, setCourseTicketSummary] = useState([]);
  const [courseTicketSummaryLoading, setCourseTicketSummaryLoading] = useState(true);
  const [courseTicketSummaryError, setCourseTicketSummaryError] = useState(null);

  const [staffLeaderboard, setStaffLeaderboard] = useState([]);
  const [staffLeaderboardLoading, setStaffLeaderboardLoading] = useState(true);
  const [staffLeaderboardError, setStaffLeaderboardError] = useState(null);

  const [conflictDays, setConflictDays] = useState(14);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [hodName, setHodName] = useState(null);
  useEffect(() => {
    axiosInstance.get('/contact-admin/hod')
      .then(({ data }) => setHodName(data?.name || null))
      .catch(() => setHodName(null));
  }, []);
  const [conflicts, setConflicts] = useState([]);
  const [conflictsLoading, setConflictsLoading] = useState(true);
  const [conflictsError, setConflictsError] = useState(null);
  const [expandedConflictCourse, setExpandedConflictCourse] = useState(null);
  const [expandedConflictDateKeys, setExpandedConflictDateKeys] = useState(() => new Set());

  const [profileRequestsCount, setProfileRequestsCount] = useState(0);
  const [profileRequestsLoading, setProfileRequestsLoading] = useState(true);
  const [profileRequestsError, setProfileRequestsError] = useState(null);

  const Spinner = ({ size = 10, color = '#10B981' }) => (
    <div className="flex items-center justify-center py-6">
      <div
        className="animate-spin rounded-full border-4 border-gray-200"
        style={{ width: size * 4, height: size * 4, borderTopColor: color }}
      />
    </div>
  );

  const user = AuthService.getUser();
  const userId = user?.superAdmin?.superAdminId;
  const userName = user?.superAdmin?.staff?.name || user?.superAdmin?.username || 'User';
  const isSuperAdmin = hasDashboardFullAccess(user) || user?.superAdmin?.staff?.type === 'admin';
  const hasFullAccess = isSuperAdmin;
  // Strict super-admin check (excludes plain "admin" staff) -- Data & AI
  // Tools is deliberately scoped tighter than the rest of this page.
  const isStrictSuperAdmin = Boolean(user?.superAdmin?.superAdminId);

  // Course-wise ticket health: shown to everyone with dashboard access.
  // Super admins get an unscoped view (every course); coordinators get one
  // scoped to just the courses they coordinate (enforced server-side).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setTicketSummaryLoading(true);
        setTicketSummaryError(null);
        const { data } = await axiosInstance.get('/tickets/coordinator-summary');
        if (!cancelled) setTicketSummary(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setTicketSummaryError("Couldn't load the ticket summary right now.");
      } finally {
        if (!cancelled) setTicketSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Course-wise Student Issues health for the "Admin Ticket Health" panel.
  // Reuses /issues/analytics (already super-admin only), whose byCourse
  // breakdown already has this exact shape.
  useEffect(() => {
    if (!isSuperAdmin) {
      setCourseTicketSummaryLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setCourseTicketSummaryLoading(true);
        setCourseTicketSummaryError(null);
        const { data } = await axiosInstance.get('/contact-admin/issues/analytics');
        const byCourse = Array.isArray(data?.byCourse) ? data.byCourse : [];
        if (!cancelled) {
          setCourseTicketSummary(
            byCourse.map((c) => ({ ...c, courseShortName: c.courseShortName || c.courseName }))
          );
        }
      } catch (err) {
        if (!cancelled) setCourseTicketSummaryError("Couldn't load the course issue summary right now.");
      } finally {
        if (!cancelled) setCourseTicketSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // Top problem solvers over the last 30 days -- visible to everyone with
  // dashboard access; the backend scopes it to coordinated courses for
  // non-super-admins the same way the ticket summary above is scoped.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStaffLeaderboardLoading(true);
        setStaffLeaderboardError(null);
        const { data } = await axiosInstance.get('/tickets/staff-leaderboard');
        if (!cancelled) setStaffLeaderboard(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setStaffLeaderboardError("Couldn't load the leaderboard right now.");
      } finally {
        if (!cancelled) setStaffLeaderboardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Today-and-future schedule conflicts (room/lecturer double-bookings) --
  // super admin only, both because the backend enforces it (403 otherwise)
  // and because it's a cross-course, all-coordinators concern that only
  // makes sense for the person who can act on any course's timetable.
  useEffect(() => {
    if (!isSuperAdmin) {
      setConflictsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setConflictsLoading(true);
        setConflictsError(null);
        const { data } = await axiosInstance.get('/admin/attendance/schedule-conflicts', {
          params: { days: conflictDays },
        });
        if (!cancelled) setConflicts(Array.isArray(data?.conflicts) ? data.conflicts : []);
      } catch (err) {
        if (!cancelled) setConflictsError("Couldn't load schedule conflicts right now.");
      } finally {
        if (!cancelled) setConflictsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin, conflictDays]);

  // Open staff-incident count for the quick-glance dashboard widget --
  // super admin only, same as the incidents feature itself.
  const [incidentCounts, setIncidentCounts] = useState({ red: 0, green: 0 });
  const [incidentCountsLoading, setIncidentCountsLoading] = useState(true);
  useEffect(() => {
    if (!isSuperAdmin) {
      setIncidentCountsLoading(false);
      return undefined;
    }
    let cancelled = false;
    axiosInstance.get('/admin/incidents')
      .then(({ data }) => { if (!cancelled) setIncidentCounts(data?.counts || { red: 0, green: 0 }); })
      .catch(() => { if (!cancelled) setIncidentCounts({ red: 0, green: 0 }); })
      .finally(() => { if (!cancelled) setIncidentCountsLoading(false); });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // Pending student-submitted profile changes (field edits + extra mobile
  // numbers + profile photos) awaiting approval. Visible to everyone -- the
  // backend scopes the actual rows to "all" for a super admin or "courses
  // this staff member coordinates" for a course coordinator, so the count
  // here already reflects only what this user can act on.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProfileRequestsLoading(true);
        setProfileRequestsError(null);
        const [fieldResp, mobileResp, imageResp] = await Promise.all([
          axiosInstance.get('/admin/students/profile-edit-requests', { params: { status: 'pending' } }),
          axiosInstance.get('/admin/students/mobile-number-requests', { params: { status: 'pending' } }),
          axiosInstance.get('/admin/students/image-edit-requests', { params: { status: 'pending' } }),
        ]);
        if (cancelled) return;
        const fieldCount = Array.isArray(fieldResp?.data?.requests) ? fieldResp.data.requests.length : 0;
        const mobileCount = Array.isArray(mobileResp?.data?.requests) ? mobileResp.data.requests.length : 0;
        const imageCount = Array.isArray(imageResp?.data?.requests) ? imageResp.data.requests.length : 0;
        setProfileRequestsCount(fieldCount + mobileCount + imageCount);
      } catch (err) {
        if (!cancelled) setProfileRequestsError("Couldn't load profile edit requests right now.");
      } finally {
        if (!cancelled) setProfileRequestsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayYmd = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // Grouped by course rather than date -- a cross-course clash (e.g. a room
  // double-booked by two different courses) is listed under both courses,
  // since either course's coordinator needs to see it.
  const conflictsByCourse = useMemo(() => {
    const byCourse = new Map();
    conflicts.forEach((c) => {
      const courseLabels = Array.from(new Set(c.entries.map((e) => e.courseLabel || 'Other')));
      courseLabels.forEach((course) => {
        if (!byCourse.has(course)) byCourse.set(course, []);
        byCourse.get(course).push(c);
      });
    });
    return Array.from(byCourse.entries())
      .map(([course, items]) => {
        const byDate = new Map();
        items.forEach((c) => {
          if (!byDate.has(c.date)) byDate.set(c.date, []);
          byDate.get(c.date).push(c);
        });
        const dates = Array.from(byDate.entries())
          .map(([date, dateItems]) => ({ date, items: dateItems }))
          .sort((a, b) => (a.date > b.date ? 1 : -1));
        return { course, items, dates };
      })
      .sort((a, b) => b.items.length - a.items.length || a.course.localeCompare(b.course));
  }, [conflicts]);

  const toggleInSet = (setter) => (key) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleConflictDateKey = toggleInSet(setExpandedConflictDateKeys);

  const formatConflictDate = (dateStr) => {
    if (dateStr === todayYmd) return 'Today';
    const d = new Date(`${dateStr}T00:00:00`);
    const tomorrow = new Date(`${todayYmd}T00:00:00`);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateStr === `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`) return 'Tomorrow';
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }).format(d);
  };

  const formatConflictTime = (value) => {
    if (!value) return '';
    const [h, m] = String(value).split(':');
    const hour = Number(h);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${m} ${period}`;
  };

  // Same "Scheduled: <date>" / "Updated: <date>" wording and Sri-Lanka-local
  // timestamp format the Timetable page already shows on each schedule cell,
  // so a coordinator sees the identical phrasing in both places.
  const formatScheduledAt = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-LK', {
      timeZone: 'Asia/Colombo',
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d);
  };

  // Plain-English "why is this a conflict" sentence for the summary table's
  // Remark column -- names the shared resource and points at whichever
  // entry was edited most recently, since that's the change to review.
  const buildConflictRemark = (conflict) => {
    const [a, b] = conflict.entries;
    const cause = conflict.entries.find((e) => e.key === conflict.lastEditedKey);
    const causeText = cause
      ? (cause.wasEverEdited
        ? `Last change: "${cause.label}" was updated by ${cause.updatedBy} on ${formatScheduledAt(cause.updatedAt)}.`
        : `Last change: "${cause.label}" was scheduled by ${cause.createdBy} on ${formatScheduledAt(cause.createdAt)}.`)
      : '';
    const reasonText = conflict.type === 'both'
      ? `Same lecturer ("${conflict.lecturerResource}") AND same room ("${conflict.roomResource}") are both booked for "${a.label}" and "${b.label}" at an overlapping time.`
      : `Same ${conflict.type === 'room' ? 'room' : 'lecturer'} ("${conflict.resource}") is booked for both "${a.label}" and "${b.label}" at an overlapping time.`;
    return `${reasonText} ${causeText}`;
  };

  const openTicketCourseDetail = async (course) => {
    setSelectedTicketCourse(course);
    setTicketCategoryDetail([]);
    setTicketCategoryLoading(true);
    try {
      const { data } = await axiosInstance.get('/tickets/coordinator-summary/categories', {
        params: { courseId: course.courseId },
      });
      setTicketCategoryDetail(Array.isArray(data) ? data : []);
    } catch (err) {
      setTicketCategoryDetail([]);
    } finally {
      setTicketCategoryLoading(false);
    }
  };

  // Exports the currently-open course's tickets, one API call for the whole
  // course, then grouped client-side into one sheet per category -- each
  // sheet lists that category's tickets with student/batch detail.
  const downloadCourseCategoryExcel = async () => {
    if (!selectedTicketCourse || allCourseTicketsDownloading) return;
    setAllCourseTicketsDownloading(true);
    try {
      const courseLabel = selectedTicketCourse.courseShortName || selectedTicketCourse.courseName;
      const { data } = await axiosInstance.get('/tickets/report', {
        params: { courseIds: selectedTicketCourse.courseId },
      });
      const tickets = Array.isArray(data) ? data : [];

      const byCategory = new Map();
      tickets.forEach((t) => {
        const key = t.categoryName || 'Uncategorized';
        if (!byCategory.has(key)) byCategory.set(key, []);
        byCategory.get(key).push(t);
      });

      const workbook = XLSX.utils.book_new();
      if (byCategory.size === 0) {
        XLSX.utils.book_append_sheet(workbook, ticketDetailWorksheet([]), 'No Data');
      } else {
        const usedSheetNames = new Set();
        Array.from(byCategory.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([categoryName, categoryTickets]) => {
            let sheetName = sanitizeSheetName(categoryName, 'Category');
            let suffix = 2;
            while (usedSheetNames.has(sheetName)) {
              sheetName = `${sanitizeSheetName(categoryName, 'Category').slice(0, 28)} ${suffix}`;
              suffix += 1;
            }
            usedSheetNames.add(sheetName);
            XLSX.utils.book_append_sheet(workbook, ticketDetailWorksheet(categoryTickets), sheetName);
          });
      }

      XLSX.writeFile(workbook, `${sanitizeFileNamePart(courseLabel)}_ticket-details-by-category.xlsx`);
    } catch (err) {
      console.error('Failed to build the course ticket-detail workbook', err);
      alert("Couldn't build the Excel file right now. Please try again.");
    } finally {
      setAllCourseTicketsDownloading(false);
    }
  };

  // Exports just one category's tickets for the currently-open course --
  // the per-category download button on each category card.
  const downloadCategoryTicketsExcel = async (category) => {
    if (!selectedTicketCourse || categoryDownloadingId) return;
    setCategoryDownloadingId(category.categoryId);
    try {
      const courseLabel = selectedTicketCourse.courseShortName || selectedTicketCourse.courseName;
      const { data } = await axiosInstance.get('/tickets/report', {
        params: { courseIds: selectedTicketCourse.courseId, categoryId: category.categoryId },
      });
      const tickets = Array.isArray(data) ? data : [];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, ticketDetailWorksheet(tickets), sanitizeSheetName(category.categoryName));
      XLSX.writeFile(
        workbook,
        `${sanitizeFileNamePart(courseLabel)}_${sanitizeFileNamePart(category.categoryName, 'category')}_tickets.xlsx`
      );
    } catch (err) {
      console.error('Failed to build the category ticket-detail workbook', err);
      alert("Couldn't build the Excel file right now. Please try again.");
    } finally {
      setCategoryDownloadingId(null);
    }
  };

  const filteredTicketSummary = useMemo(() => {
    if (selectedTicketCourseIds.includes('all')) return ticketSummary;
    return ticketSummary.filter((c) => selectedTicketCourseIds.includes(String(c.courseId)));
  }, [ticketSummary, selectedTicketCourseIds]);

  const ticketTotals = useMemo(
    () => filteredTicketSummary.reduce(
      (acc, c) => ({
        open: acc.open + (c.open || 0),
        inProgress: acc.inProgress + (c.inProgress || 0),
        resolved: acc.resolved + (c.resolved || 0),
        closed: acc.closed + (c.closed || 0),
      }),
      { open: 0, inProgress: 0, resolved: 0, closed: 0 }
    ),
    [filteredTicketSummary]
  );

  const courseTicketTotals = useMemo(
    () => courseTicketSummary.reduce(
      (acc, a) => ({
        open: acc.open + (a.open || 0),
        inProgress: acc.inProgress + (a.inProgress || 0),
      }),
      { open: 0, inProgress: 0 }
    ),
    [courseTicketSummary]
  );

  const selectedTicketCourseName = useMemo(() => {
    if (selectedTicketCourseIds.includes('all') || selectedTicketCourseIds.length === 0) return 'All Courses';
    if (selectedTicketCourseIds.length === 1) {
      const match = ticketSummary.find((c) => String(c.courseId) === selectedTicketCourseIds[0]);
      return match?.courseName || '1 course selected';
    }
    return `${selectedTicketCourseIds.length} courses selected`;
  }, [selectedTicketCourseIds, ticketSummary]);

  useEffect(() => {
    let cancelled = false;

    const loadUnscheduledBatchNotification = async () => {
      if (!userId) return;

      const todayStr = new Date().toISOString().slice(0, 10);
      const sessionKey = `unscheduled-batches-notified-${todayStr}`;
      if (sessionStorage.getItem(sessionKey)) return;

      try {
        const res = await axiosInstance.get('/admin/timetable/unscheduled-batches', {
          params: { date: todayStr },
        });
        if (cancelled) return;

        const missing = Array.isArray(res?.data?.unscheduledBatches) ? res.data.unscheduledBatches : [];
        const scheduleType = res?.data?.scheduleType || 'weekday';
        if (missing.length === 0) return;

        const byCourse = {};
        missing.forEach((batch) => {
          const courseName = batch.courseName || 'Unknown Course';
          const batchName = batch.batchInternalName || batch.batchName;
          if (!byCourse[courseName]) byCourse[courseName] = [];
          byCourse[courseName].push(batchName);
        });

        const courseCount = Object.keys(byCourse).length;
        const title = `${missing.length} ${scheduleType} batch${missing.length > 1 ? 'es' : ''} unscheduled — ${todayStr}`;
        const message = `${courseCount} course${courseCount > 1 ? 's' : ''} affected today. Open to review and assign timetable entries.`;
        const link = `/admin/notifications/unscheduled-schedules?date=${encodeURIComponent(todayStr)}`;
        const localNotification = {
          id: `local-unscheduled-${todayStr}`,
          title,
          message,
          type: 'warning',
          link,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        sessionStorage.setItem(sessionKey, 'pending');

        NotificationService.saveLocalNotification(userId, localNotification);
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));

        await axiosInstance.post('/admin/timetable/notify-unscheduled', { date: todayStr, userId });
        if (cancelled) return;

        sessionStorage.setItem(sessionKey, 'sent');
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));
      } catch (_) {
        sessionStorage.removeItem(`unscheduled-batches-notified-${new Date().toISOString().slice(0, 10)}`);
      }
    };

    loadUnscheduledBatchNotification();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ── Live notification: batches with 0 < scheduledHours < 3 ───────────────
  useEffect(() => {
    let cancelled = false;

    const loadLowHoursNotification = async () => {
      if (!userId) return;

      const todayStr = new Date().toISOString().slice(0, 10);
      const sessionKey = `low-hours-batches-notified-${todayStr}`;
      if (sessionStorage.getItem(sessionKey)) return;

      try {
        const res = await axiosInstance.get('/admin/timetable/low-hours-batches', {
          params: { date: todayStr },
        });
        if (cancelled) return;

        const batches = Array.isArray(res?.data?.batches) ? res.data.batches : [];
        if (batches.length === 0) return;

        const byCourse = {};
        batches.forEach((batch) => {
          const courseName = batch.courseName || 'Unknown Course';
          if (!byCourse[courseName]) byCourse[courseName] = [];
          byCourse[courseName].push(batch.batchInternalName || batch.batchName);
        });

        const courseCount = Object.keys(byCourse).length;
        const title = `${batches.length} batch${batches.length > 1 ? 'es' : ''} scheduled with less than 3 hours — ${todayStr}`;
        const message = `${courseCount} course${courseCount > 1 ? 's' : ''} affected today. Open to review scheduled hours.`;
        const link = `/admin/notifications/low-hours-schedules?date=${encodeURIComponent(todayStr)}`;
        const localNotification = {
          id: `local-low-hours-${todayStr}`,
          title,
          message,
          type: 'warning',
          link,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        sessionStorage.setItem(sessionKey, 'pending');
        NotificationService.saveLocalNotification(userId, localNotification);
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));

        await axiosInstance.post('/admin/timetable/notify-low-hours', { date: todayStr, userId });
        if (cancelled) return;

        sessionStorage.setItem(sessionKey, 'sent');
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));
      } catch (_) {
        sessionStorage.removeItem(`low-hours-batches-notified-${new Date().toISOString().slice(0, 10)}`);
      }
    };

    loadLowHoursNotification();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;

        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
      } catch (e) {
        console.error('Failed to load coordinate courses for Dashboard', e);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/stats');
        if (!mounted) return;
        const d = resp?.data || {};
        setStats(d.stats || []);
      } catch (e) {
        if (!mounted) return;
        setStatsError(e?.response?.data?.message || 'Failed to load dashboard stats');
      } finally {
        if (mounted) setStatsLoading(false);
      }
    };

    const fetchAttendance = async () => {
      setAttendanceLoading(true);
      setAttendanceError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/attendance');
        if (!mounted) return;
        const d = resp?.data || {};
        setCourses(d.courses || []);
        setAllCoursesAttendanceData(d.allCoursesAttendanceData || []);
        setCourseAttendanceData(d.courseAttendanceData || []);
      } catch (e) {
        if (!mounted) return;
        setAttendanceError(e?.response?.data?.message || 'Failed to load dashboard attendance');
      } finally {
        if (mounted) setAttendanceLoading(false);
      }
    };

    const fetchDistribution = async () => {
      setDistributionLoading(true);
      setDistributionError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/course-distribution');
        if (!mounted) return;
        const d = resp?.data || {};
        setBatchCountData(d.batchCountData || []);
        setBengCountData(d.bengCountData || []);
        setBengCncsCountData(d.bengCncsCountData || []);
        setL3CountData(d.l3CountData || []);
        setCitCountData(d.citCountData || []);
        setEnglishDiplomaCountData(d.englishDiplomaCountData || []);
        setHndBusinessCountData(d.hndBusinessCountData || []);
        setBaBusinessAdminCountData(d.baBusinessAdminCountData || []);
        setL7SmlCountData(d.l7SmlCountData || []);
        setHndDigitalTechCountData(d.hndDigitalTechCountData || []);
      } catch (e) {
        if (!mounted) return;
        setDistributionError(e?.response?.data?.message || 'Failed to load dashboard course distribution');
      } finally {
        if (mounted) setDistributionLoading(false);
      }
    };

    fetchStats();
    fetchAttendance();
    fetchDistribution();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const iconMap = {
    users: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    'user-group': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    'user-check': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    calendar: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    'check-circle': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return '🌅 Good Morning';
    if (hour < 17) return '☀️ Good Afternoon';
    return '🌙 Good Evening';
  };

  const formatDate = () =>
    currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const statsWithIcons = useMemo(() => (stats || []).map((s) => ({
    ...s,
    iconElement: iconMap[s.icon] || iconMap.users,
  })), [stats]);

  const allowedCourseIdsSet = useMemo(() => {
    if (!Array.isArray(accessibleCourseIds) || accessibleCourseIds.length === 0) return null;
    const set = new Set();
    accessibleCourseIds.forEach((id) => {
      const num = Number(id);
      if (Number.isFinite(num)) set.add(num);
    });
    return set;
  }, [accessibleCourseIds]);

  const filteredAttendanceCourses = useMemo(() => {
    const base = Array.isArray(courses) ? courses : [];
    if (!allowedCourseIdsSet || allowedCourseIdsSet.size === 0) return base;
    return base.filter((c) => {
      const numId = Number(c.courseId);
      return Number.isFinite(numId) && allowedCourseIdsSet.has(numId);
    });
  }, [courses, allowedCourseIdsSet]);

  const courseIdMapping = {
    hnd: [2, 3, 4, 5, 6, 7],
    beng: [1],
    bengCncs: [20],
    l3: [8, 9, 12, 13],
    cit: [10, 11],
    englishDiploma: [14, 15],
    hndBusiness: [16, 17],
    baBusiness: [18],
    l7Sml: [19],
  };

  const courseOptions = [
    { value: 'hnd', label: 'HND - IT Software Engineering & Network Engineering', data: batchCountData, type: 'dual', courseIds: courseIdMapping.hnd },
    { value: 'hndDT', label: 'HND - Digital Technologies (DA & CS)', data: hndDigitalTechCountData, type: 'dual-dt', courseIds: [], alwaysShow: true },
    { value: 'beng', label: 'BEng (Hons) in Software Engineering', data: bengCountData, type: 'single', courseIds: courseIdMapping.beng },
    { value: 'bengCncs', label: 'BEng (Hons) Computer Networking and Cloud Security', data: bengCncsCountData, type: 'single', courseIds: courseIdMapping.bengCncs },
    { value: 'l3', label: 'Level 3 Diploma in Information', data: l3CountData, type: 'single', courseIds: courseIdMapping.l3 },
    { value: 'cit', label: 'Certificate In Information Technology', data: citCountData, type: 'single', courseIds: courseIdMapping.cit },
    { value: 'englishDiploma', label: 'Diploma in English', data: englishDiplomaCountData, type: 'single', courseIds: courseIdMapping.englishDiploma },
    { value: 'hndBusiness', label: 'Pearson BTEC Level 5 HND in Business', data: hndBusinessCountData, type: 'single', courseIds: courseIdMapping.hndBusiness },
    { value: 'baBusiness', label: 'BA (Hons) in Business Administration', data: baBusinessAdminCountData, type: 'single', courseIds: courseIdMapping.baBusiness },
    { value: 'l7Sml', label: 'Level 7 BTEC Diploma in Strategic Management and Leadership', data: l7SmlCountData, type: 'single', courseIds: courseIdMapping.l7Sml },
  ].filter((course) => course.alwaysShow || (course.data && course.data.length > 0));

  const currentCourse = courseOptions.find((c) => c.value === selectedCourse) || courseOptions[0];

  const currentAttendanceData = useMemo(() => {
    if (selectedAttendanceCourses.includes('all')) return allCoursesAttendanceData;
    const selected = courseAttendanceData.filter((c) => selectedAttendanceCourses.includes(String(c.courseId)));
    if (selected.length === 0) return [];
    if (selected.length === 1) return selected[0].data;
    const map = new Map();
    selected.forEach((cd) => {
      cd.data.forEach((item) => {
        const k = item.fullDate;
        if (map.has(k)) map.get(k).count += item.count; else map.set(k, { ...item });
      });
    });
    return Array.from(map.values()).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [selectedAttendanceCourses, allCoursesAttendanceData, courseAttendanceData]);

  const selectedAttendanceCourseName = useMemo(() => {
    if (selectedAttendanceCourses.includes('all')) return hasFullAccess ? 'All Courses (Total Branch Visitors)' : 'All Accessible Courses';
    const names = [];
    selectedAttendanceCourses.forEach((idStr) => {
      const att = courseAttendanceData.find((c) => String(c.courseId) === idStr);
      if (att) names.push(att.courseName); else {
        const c = courses.find((x) => String(x.courseId) === idStr);
        if (c) names.push(c.fullName);
      }
    });
    if (names.length === 0) return 'No courses selected';
    if (names.length === 1) return names[0];
    return `${names.length} Courses Selected`;
  }, [selectedAttendanceCourses, courseAttendanceData, courses, hasFullAccess]);

  const totalAttendanceCount = currentAttendanceData.reduce((s, d) => s + (d.count || 0), 0);
  const avgAttendanceCount = currentAttendanceData.length ? Math.round(totalAttendanceCount / currentAttendanceData.length) : 0;
  const sectionErrors = [statsError, attendanceError, distributionError].filter(Boolean);

  useEffect(() => {
    if (courseOptions.length > 0) {
      const exists = courseOptions.some((c) => c.value === selectedCourse);
      if (!exists) setSelectedCourse(courseOptions[0].value);
    }
  }, [courseOptions, selectedCourse]);

  const EnhancedChartSection = ({ title, data, barColor, gradientId, description }) => {
    const totalStudents = data.reduce((sum, d) => sum + (d.count || 0), 0);
    return (
      <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent">{title}</h3>
            {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}
          </div>
          <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 px-6 py-3">
            <div className="text-center">
              <p className="text-xs font-medium text-gray-600">Total Students</p>
              <p className="mt-1 text-2xl font-bold" style={{ color: barColor }}>{totalStudents}</p>
            </div>
          </div>
        </div>
        <div className="mb-4 text-xs text-gray-600"><span className="font-medium">{data.length}</span> Active Batches</div>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={barColor} stopOpacity={0.9} />
                <stop offset="95%" stopColor={barColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
            <Tooltip cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            <Bar dataKey="count" name="Total Students" fill={`url(#${gradientId})`} radius={[8, 8, 0, 0]}>
              <LabelList dataKey="count" position="top" style={{ fontSize: '12px', fill: barColor, fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <DashboardLayout title="Dashboard" showStaffPresenceButton>
      {sectionErrors.length > 0 && (
        <div className="mb-6 space-y-3">
          {sectionErrors.map((msg, idx) => (
            <div key={`${msg}-${idx}`} className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800">{msg}</div>
          ))}
        </div>
      )}
      
      {/* Welcome Banner - Always show */}
      <div className="mb-8 rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 text-white shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="space-y-3">
                <h2 className="text-3xl font-bold tracking-tight">{getGreeting()}, {userName}!</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/30 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm border border-white/30 shadow-lg">
                    <span className="text-base">{isSuperAdmin ? '👑 Admin' : '👤 Staff Member'}</span>
                  </span>
                </div>
                <p className="text-lg text-indigo-100">{formatDate()}</p>
                <p className="mt-4 max-w-2xl text-base text-indigo-50">Here's an overview of your institution's performance and key metrics.</p>
                {isStrictSuperAdmin && (
                  <a
                    href="/admin/data-tools"
                    className="mt-2 inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm border border-white/30 hover:bg-white/30"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" /></svg>
                    Database Activity
                  </a>
                )}
              </div>
              <div className="hidden md:block">
                <div className="rounded-full bg-white/20 p-6 backdrop-blur-sm">
                  <svg className="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Student Profile Edit Requests, Staff Incidents, and Schedule
              Conflicts sit side by side as three columns for super admins
              (who see all three); everyone else only ever has the first
              card, so the grid collapses to a single full-width column. */}
          <div className={`mb-8 grid grid-cols-1 gap-6 ${isSuperAdmin ? 'lg:grid-cols-3' : ''}`}>
          {/* Student Profile Edit Requests -- visible to everyone; a course
              coordinator only ever sees/approves requests for their own
              courses (enforced server-side). Compact summary card (count +
              CTA) rather than the full review table, which lives on its own
              page for a focused review workflow. */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">Student Profile Edit Requests</h3>
                  {profileRequestsLoading ? (
                    <p className="mt-0.5 text-xs text-gray-500">Checking for pending student changes…</p>
                  ) : profileRequestsError ? (
                    <p className="mt-0.5 text-xs font-medium text-rose-600">{profileRequestsError}</p>
                  ) : profileRequestsCount === 0 ? (
                    <p className="mt-0.5 text-xs font-medium text-emerald-600">✅ Nothing waiting on your review.</p>
                  ) : (
                    <p className="mt-0.5 text-xs font-medium text-gray-500">
                      <span className="font-bold text-indigo-600">{profileRequestsCount}</span> change{profileRequestsCount === 1 ? '' : 's'} to student profile details or mobile numbers awaiting approval
                    </p>
                  )}
                </div>
              </div>
              <a
                href="/admin/student-profile-edit-requests"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
              >
                {profileRequestsCount > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/25 px-1.5 text-xs font-bold">
                    {profileRequestsCount}
                  </span>
                )}
                Review Requests →
              </a>
            </div>
          </div>

          {/* Staff Incidents quick-glance -- super admin only. The entry
              point for recording a new incident lives here; the full list,
              filtering, comments, and flag management live on its own page. */}
          {isSuperAdmin && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="flex items-center gap-2 text-base font-bold text-gray-800">
                    <span className="text-red-600">🚩</span> Staff Incidents
                  </h3>
                  {incidentCountsLoading ? (
                    <p className="mt-0.5 text-xs text-gray-500">Loading…</p>
                  ) : incidentCounts.red === 0 ? (
                    <p className="mt-0.5 text-xs font-medium text-emerald-600">✅ No open (red-flag) incidents.</p>
                  ) : (
                    <p className="mt-0.5 text-xs font-medium text-gray-500">
                      <span className="font-bold text-red-600">{incidentCounts.red}</span> open red-flag incident{incidentCounts.red === 1 ? '' : 's'} · <span className="font-bold text-gray-700">{incidentCounts.green}</span> resolved
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="/admin/incidents?new=1"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-red-700"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    New Incident
                  </a>
                  <a
                    href="/admin/incidents"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-gray-700"
                  >
                    View All
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Schedule Conflicts -- super admin only, today + upcoming days.
              Two-level disclosure (course, then individual conflict) so the
              panel stays compact by default and only expands on demand. */}
          {isSuperAdmin && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
              <div className="mb-4 flex flex-col gap-3">
                <div>
                  <h3 className="flex items-center gap-2 text-base font-bold text-gray-800">
                    <span className="text-rose-600">⚠️</span> Schedule Conflicts
                  </h3>
                  {conflictsLoading ? (
                    <p className="mt-0.5 text-xs text-gray-500">Scanning the timetable for room/lecturer clashes…</p>
                  ) : conflictsError ? (
                    <p className="mt-0.5 text-xs font-medium text-rose-600">{conflictsError}</p>
                  ) : conflicts.length === 0 ? (
                    <p className="mt-0.5 text-xs font-medium text-emerald-600">✅ No room or lecturer double-bookings in the next {conflictDays} days.</p>
                  ) : (
                    <p className="mt-0.5 text-xs font-medium text-gray-500">
                      <span className="font-bold text-rose-600">{conflicts.length}</span> conflict{conflicts.length === 1 ? '' : 's'} across <span className="font-bold text-gray-700">{conflictsByCourse.length}</span> course{conflictsByCourse.length === 1 ? '' : 's'} · click a course, then a conflict, to see who to notify
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    id="conflict-days"
                    value={conflictDays}
                    onChange={(e) => { setConflictDays(Number(e.target.value)); setExpandedConflictCourse(null); setExpandedConflictDateKeys(new Set()); }}
                    className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  >
                    <option value={7}>Next 7 days</option>
                    <option value={14}>Next 14 days</option>
                    <option value={30}>Next 30 days</option>
                    <option value={60}>Next 60 days</option>
                  </select>
                  {conflicts.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowConflictModal(true)}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-rose-700"
                    >
                      View Details
                    </button>
                  )}
                  <a
                    href="/admin/schedule"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-gray-700"
                  >
                    Open Timetable →
                  </a>
                </div>
              </div>

              {showConflictModal && conflicts.length > 0 && (
                <ScheduleConflictModal conflicts={conflicts} onClose={() => setShowConflictModal(false)} />
              )}

              {conflictsLoading ? (
                <Spinner color="#e11d48" />
              ) : conflicts.length > 0 && (
                <div className="space-y-1.5">
                  {conflictsByCourse.map(({ course, items, dates }) => {
                    const isCourseExpanded = expandedConflictCourse === course;
                    return (
                      <div key={course} className="overflow-hidden rounded-lg border border-gray-200">
                        <button
                          type="button"
                          onClick={() => setExpandedConflictCourse(isCourseExpanded ? null : course)}
                          className="flex w-full items-center justify-between gap-3 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">{items.length}</span>
                            <span className="text-sm font-semibold text-gray-800">{course}</span>
                          </div>
                          <svg className={`h-4 w-4 text-gray-400 transition-transform ${isCourseExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isCourseExpanded && (
                          <div className="space-y-1.5 border-t border-gray-200 p-2">
                            {dates.map(({ date, items: dateItems }) => {
                              const dateKey = `${course}::${date}`;
                              const isDateExpanded = expandedConflictDateKeys.has(dateKey);
                              const isToday = date === todayYmd;
                              return (
                                <div key={dateKey} className="overflow-hidden rounded-lg border border-gray-200">
                                  <button
                                    type="button"
                                    onClick={() => toggleConflictDateKey(dateKey)}
                                    className="flex w-full items-center justify-between gap-2 bg-white px-2.5 py-1.5 text-left hover:bg-gray-50"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${isToday ? 'bg-rose-600 text-white' : 'bg-gray-700 text-white'}`}>
                                        {formatConflictDate(date)}
                                      </span>
                                      <span className="text-xs font-semibold text-gray-700">{dateItems.length} conflict{dateItems.length === 1 ? '' : 's'}</span>
                                    </div>
                                    <svg className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${isDateExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {isDateExpanded && (
                                    <div className="overflow-x-auto border-t border-gray-200">
                                      <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-50 text-gray-500">
                                          <tr>
                                            <th className="px-2.5 py-1.5 font-semibold">#</th>
                                            <th className="px-2.5 py-1.5 font-semibold">Clash</th>
                                            <th className="px-2.5 py-1.5 font-semibold">Time</th>
                                            <th className="px-2.5 py-1.5 font-semibold">Entry A</th>
                                            <th className="px-2.5 py-1.5 font-semibold">Entry B</th>
                                            <th className="px-2.5 py-1.5 font-semibold">Remark</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {dateItems.map((conflict, idx) => {
                                            const [a, b] = conflict.entries;
                                            return (
                                              <tr key={`${dateKey}::${idx}`} className="align-top hover:bg-gray-50/70">
                                                <td className="px-2.5 py-2 text-gray-400">{idx + 1}</td>
                                                <td className="px-2.5 py-2">
                                                  <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ${conflict.type === 'both' ? 'bg-fuchsia-100 text-fuchsia-700' : conflict.type === 'room' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}>
                                                    {conflict.type === 'both'
                                                      ? <>🏫👨‍🏫 {conflict.roomResource} &amp; {conflict.lecturerResource}</>
                                                      : <>{conflict.type === 'room' ? '🏫' : '👨‍🏫'} {conflict.resource}</>}
                                                  </span>
                                                </td>
                                                <td className="whitespace-nowrap px-2.5 py-2 text-gray-600">
                                                  {formatConflictTime(a.startTime)} – {formatConflictTime(a.endTime)}
                                                </td>
                                                <td className={`px-2.5 py-2 ${a.key === conflict.lastEditedKey ? 'rounded bg-amber-50 font-medium text-amber-800' : 'text-gray-700'}`}>
                                                  {a.label}
                                                </td>
                                                <td className={`px-2.5 py-2 ${b.key === conflict.lastEditedKey ? 'rounded bg-amber-50 font-medium text-amber-800' : 'text-gray-700'}`}>
                                                  {b.label}
                                                </td>
                                                <td className="min-w-[220px] px-2.5 py-2 text-gray-500">{buildConflictRemark(conflict)}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </div>

          {/* Stats Section with Loader */}
          {statsLoading ? (
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 space-y-3">
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                      <div className="h-10 w-20 animate-pulse rounded bg-gray-300"></div>
                    </div>
                    <div className="h-16 w-16 animate-pulse rounded-2xl bg-gray-200"></div>
                  </div>
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {statsWithIcons.map((stat, index) => (
                <div key={stat.name} className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-10"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                      <p className="mt-2 text-4xl font-bold text-gray-900 transition-all duration-300 group-hover:scale-110">{stat.value}</p>
                    </div>
                    <div className="rounded-2xl bg-indigo-600 p-4 text-white shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">{stat.iconElement}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isSuperAdmin && (
            <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-2xl font-bold text-transparent">🛡️ HOD Ticket Health{hodName ? ` · ${hodName}` : ''}</h3>
                  <p className="mt-0.5 text-xs text-gray-400">Student Issues by course</p>
                  {courseTicketSummaryLoading ? (
                    <p className="mt-2 text-sm text-gray-500">Loading student issues…</p>
                  ) : courseTicketSummary.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-500">No student issues have been raised yet.</p>
                  ) : courseTicketTotals.open + courseTicketTotals.inProgress === 0 ? (
                    <p className="mt-2 text-sm font-medium text-emerald-600">🎉 Every student issue is resolved or closed.</p>
                  ) : (
                    <p className="mt-2 text-sm font-medium text-gray-700">
                      🔥 <span className="font-bold text-rose-600">{courseTicketTotals.open + courseTicketTotals.inProgress}</span> issue{courseTicketTotals.open + courseTicketTotals.inProgress === 1 ? '' : 's'} still open or in progress
                    </p>
                  )}
                </div>
              </div>

              {courseTicketSummaryLoading ? (
                <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/40 p-10">
                  <div className="flex items-end justify-around gap-4" style={{ height: 300 }}>
                    {[120, 200, 90, 260, 150].map((h, i) => (
                      <div key={i} className="w-12 animate-pulse rounded-t-lg bg-rose-200" style={{ height: h }} />
                    ))}
                  </div>
                </div>
              ) : courseTicketSummaryError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{courseTicketSummaryError}</div>
              ) : courseTicketSummary.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-400">
                  Nothing to show yet — student issues will appear here once they're raised.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={courseTicketSummary} margin={{ top: 20, right: 30, left: 20, bottom: 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="courseShortName" angle={-30} textAnchor="end" height={110} interval={0} tick={{ fontSize: 11, fill: '#6B7280' }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} label={{ value: 'Issues', angle: -90, position: 'insideLeft', style: { fill: '#6B7280' } }} />
                    <Tooltip cursor={{ fill: 'rgba(244, 63, 94, 0.06)' }} itemSorter={(item) => TICKET_STATUS_ORDER[item.dataKey] ?? 99} />
                    <Legend content={<TicketStatusLegend />} />
                    {/* Same bottom-to-top declaration order as the course-wise chart below. */}
                    <Bar dataKey="closed" name="Closed" stackId="tickets" fill="#94a3b8" />
                    <Bar dataKey="resolved" name="Resolved" stackId="tickets" fill="#10b981" />
                    <Bar dataKey="inProgress" name="In Progress" stackId="tickets" fill="#0ea5e9" />
                    <Bar dataKey="open" name="Open" stackId="tickets" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="total" position="top" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-2xl font-bold text-transparent">🎫 Ticket Health — Course Wise</h3>
                  {ticketSummaryLoading ? (
                    <p className="mt-2 text-sm text-gray-500">Loading your courses' ticket health…</p>
                  ) : ticketSummary.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-500">No tickets have come in yet for the courses you coordinate.</p>
                  ) : ticketTotals.open + ticketTotals.inProgress === 0 ? (
                    <p className="mt-2 text-sm font-medium text-emerald-600">🎉 All caught up! Every ticket across your courses is resolved or closed. Amazing work!</p>
                  ) : (
                    <p className="mt-2 text-sm font-medium text-gray-700">
                      🔥 <span className="font-bold text-rose-600">{ticketTotals.open + ticketTotals.inProgress}</span> ticket{ticketTotals.open + ticketTotals.inProgress === 1 ? '' : 's'} need your attention{!selectedTicketCourseIds.includes('all') ? '' : ` across ${ticketSummary.length} course${ticketSummary.length === 1 ? '' : 's'}`} — let's clear the queue!
                    </p>
                  )}
                </div>
                {ticketSummary.length > 0 && (
                  <div className="sm:w-80">
                    <label className="mb-2 block text-sm font-medium text-gray-700">Filter by Course</label>
                    <button type="button" onClick={() => setIsTicketCourseFilterModalOpen(true)} className="flex w-full items-center justify-between rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm">
                      <span className="flex items-center gap-2 truncate">{selectedTicketCourseName}</span>
                      <svg className="h-5 w-5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </div>
                )}
              </div>

              {ticketSummaryLoading ? (
                <div className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/40 p-10">
                  <div className="flex items-end justify-around gap-4" style={{ height: 300 }}>
                    {[120, 200, 90, 260, 150].map((h, i) => (
                      <div key={i} className="w-12 animate-pulse rounded-t-lg bg-violet-200" style={{ height: h }} />
                    ))}
                  </div>
                </div>
              ) : ticketSummaryError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{ticketSummaryError}</div>
              ) : ticketSummary.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-400">
                  Nothing to show right now — check back once students start raising tickets.
                </div>
              ) : filteredTicketSummary.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-400">
                  No courses match your current filter.
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={filteredTicketSummary} margin={{ top: 20, right: 30, left: 20, bottom: 90 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="courseShortName" angle={-30} textAnchor="end" height={110} interval={0} tick={{ fontSize: 11, fill: '#6B7280' }} />
                      <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} label={{ value: 'Tickets', angle: -90, position: 'insideLeft', style: { fill: '#6B7280' } }} />
                      <Tooltip
                        cursor={{ fill: 'rgba(139, 92, 246, 0.06)' }}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.courseName || label}
                        itemSorter={(item) => TICKET_STATUS_ORDER[item.dataKey] ?? 99}
                      />
                      <Legend content={<TicketStatusLegend />} />
                      {/* Declared bottom-to-top (Recharts stacks in declaration order) so the
                          stack reads Open, In Progress, Resolved, Closed from top to bottom --
                          the order tickets actually move through, most-urgent on top. */}
                      <Bar dataKey="closed" name="Closed" stackId="tickets" fill="#94a3b8" cursor="pointer" onClick={(data) => openTicketCourseDetail(data.payload)} />
                      <Bar dataKey="resolved" name="Resolved" stackId="tickets" fill="#10b981" cursor="pointer" onClick={(data) => openTicketCourseDetail(data.payload)} />
                      <Bar dataKey="inProgress" name="In Progress" stackId="tickets" fill="#0ea5e9" cursor="pointer" onClick={(data) => openTicketCourseDetail(data.payload)} />
                      <Bar dataKey="open" name="Open" stackId="tickets" fill="#f59e0b" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(data) => openTicketCourseDetail(data.payload)}>
                        <LabelList dataKey="total" position="top" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="mt-1 text-center text-xs text-gray-400">Click any bar to see that course's ticket categories in detail</p>
                </>
              )}
          </div>

          {hasFullAccess && (
          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-2xl font-bold text-transparent">📊 Last 30 Days Attendance Trends</h3>
                <p className="mt-2 text-sm text-gray-500">{selectedAttendanceCourses.includes('all') ? 'Total unique visitors to the branch (deduplicated by student)' : selectedAttendanceCourses.length === 1 ? 'Course-specific attendance (unique students per day)' : `Combined attendance from ${selectedAttendanceCourses.length} selected courses`}</p>
              </div>
              <div className="sm:w-96">
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Filter by Course</label>
                  {hasFullAccess ? (
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">All Courses</span>
                  ) : (
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">Selected</span>
                  )}
                </div>
                <button type="button" onClick={() => setIsCourseFilterModalOpen(true)} className="flex w-full items-center justify-between rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm">
                  <span className="flex items-center gap-2">{selectedAttendanceCourses.includes('all') ? 'All Courses' : selectedAttendanceCourseName}</span>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>
            </div>

            {attendanceLoading ? (
              <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-10">
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[1, 2, 3].map((k) => (
                    <div key={k} className="rounded-xl bg-white p-4 shadow-sm">
                      <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                      <div className="mt-3 h-8 w-24 animate-pulse rounded bg-gray-300" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <Spinner size={12} />
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-4"><p className="text-xs font-medium text-gray-600">Total Attendances (30 days)</p><p className="mt-1 text-3xl font-bold text-emerald-600">{totalAttendanceCount.toLocaleString()}</p></div>
                  <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-4"><p className="text-xs font-medium text-gray-600">Average Per Day</p><p className="mt-1 text-3xl font-bold text-blue-600">{avgAttendanceCount.toLocaleString()}</p></div>
                  <div className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 p-4"><p className="text-xs font-medium text-gray-600">Peak Day Attendance</p><p className="mt-1 text-3xl font-bold text-purple-600">{Math.max(...currentAttendanceData.map((d) => d.count || 0), 0).toLocaleString()}</p></div>
                </div>

                <ResponsiveContainer width="100%" height={450}>
                  <AreaChart data={currentAttendanceData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <defs>
                      <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} label={{ value: 'Unique Students', angle: -90, position: 'insideLeft', style: { fill: '#6B7280' } }} />
                    <Tooltip cursor={{ stroke: '#10B981', strokeWidth: 2 }} />
                    <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={3} fill="url(#colorAttendance)" name="Unique Students" />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
          )}

          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="mb-6">
              <h3 className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-2xl font-bold text-transparent">🏆 Top Problem Solvers</h3>
              <p className="mt-2 text-sm text-gray-500">Tickets resolved in the last 30 days, ranked highest to lowest</p>
            </div>

            {staffLeaderboardLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((k) => (
                  <div key={k} className="flex items-center gap-4 rounded-xl border border-dashed border-amber-200 bg-amber-50/40 p-4">
                    <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                      <div className="h-2 w-full animate-pulse rounded-full bg-gray-100" />
                    </div>
                    <div className="h-6 w-10 animate-pulse rounded-full bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : staffLeaderboardError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{staffLeaderboardError}</div>
            ) : staffLeaderboard.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-400">
                No tickets have been resolved in the last 30 days yet.
              </div>
            ) : (
              <div className="space-y-2">
                {staffLeaderboard.map((staff, index) => {
                  const topCount = staffLeaderboard[0]?.resolvedCount || 1;
                  const barPct = Math.max(6, Math.round((staff.resolvedCount / topCount) * 100));
                  const rankBadge = index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-gray-200 text-gray-700' : index === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500';
                  const initials = (staff.name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
                  return (
                    <div key={staff.staffId} className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition hover:shadow-sm">
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${rankBadge}`}>{index + 1}</span>

                      {staff.imagePath ? (
                        <img src={getImageURL(staff.imagePath)} alt={staff.name} className="h-10 w-10 shrink-0 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">{initials}</div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-800">{staff.name}</p>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${barPct}%` }} />
                        </div>
                      </div>

                      <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-700">{staff.resolvedCount}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {isTicketCourseFilterModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
              <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                <div className="border-b border-gray-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-violet-500 p-2 text-white"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg></div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Filter Tickets by Course</h3>
                        <p className="text-sm text-gray-600">Select one or more of your coordinated courses</p>
                      </div>
                    </div>
                    <button onClick={() => setIsTicketCourseFilterModalOpen(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                  </div>
                </div>
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg></div>
                    <input type="text" value={ticketCourseSearchQuery} onChange={(e) => setTicketCourseSearchQuery(e.target.value)} placeholder="Search your courses..." className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
                    {ticketCourseSearchQuery && (<button onClick={() => setTicketCourseSearchQuery('')} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>)}
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto px-6 py-4">
                  {(() => {
                    const visibleCourses = ticketSummary.filter((c) => {
                      if (!ticketCourseSearchQuery) return true;
                      return (c.courseName || '').toLowerCase().includes(ticketCourseSearchQuery.toLowerCase());
                    });
                    const allVisibleIds = visibleCourses.map((c) => String(c.courseId));
                    const isAllChecked = selectedTicketCourseIds.includes('all') || (allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedTicketCourseIds.includes(id)));
                    const isSomeChecked = !isAllChecked && allVisibleIds.some((id) => selectedTicketCourseIds.includes(id));
                    return (
                      <div className="space-y-2">
                        <label className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 ${isAllChecked ? 'border-violet-300 bg-violet-50' : isSomeChecked ? 'border-violet-200 bg-violet-50/40' : 'border-gray-200 bg-white hover:border-violet-200'}`}>
                          <input
                            type="checkbox"
                            checked={isAllChecked}
                            ref={(el) => { if (el) el.indeterminate = isSomeChecked; }}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedTicketCourseIds(['all']);
                              else setSelectedTicketCourseIds([]);
                            }}
                            className="h-5 w-5 rounded border-gray-300 text-violet-600 focus:ring-2 focus:ring-violet-500"
                          />
                          <span className={`flex-1 font-semibold ${isAllChecked || isSomeChecked ? 'text-violet-900' : 'text-gray-900'}`}>
                            {ticketCourseSearchQuery ? `Select All Matching (${visibleCourses.length})` : `Select All Courses (${visibleCourses.length})`}
                          </span>
                          {isAllChecked && (
                            <div className="rounded-full bg-violet-500 p-1"><svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>
                          )}
                        </label>
                        {visibleCourses.map((course) => {
                          const idStr = String(course.courseId);
                          const isSelected = selectedTicketCourseIds.includes('all') || selectedTicketCourseIds.includes(idStr);
                          return (
                            <label key={idStr} className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 ${isSelected ? 'border-violet-300 bg-violet-50' : 'border-gray-200 bg-white hover:border-violet-200'}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTicketCourseIds((prev) => [...new Set([...prev.filter((x) => x !== 'all'), idStr])]);
                                  } else {
                                    setSelectedTicketCourseIds((prev) => (prev.includes('all') ? allVisibleIds.filter((x) => x !== idStr) : prev.filter((x) => x !== idStr)));
                                  }
                                }}
                                className="h-5 w-5 rounded border-gray-300 text-violet-600 focus:ring-2 focus:ring-violet-500"
                              />
                              <span className={`flex-1 font-semibold ${isSelected ? 'text-violet-900' : 'text-gray-900'}`}>{course.courseName}</span>
                              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">{course.open + course.inProgress} pending</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <button onClick={() => setIsTicketCourseFilterModalOpen(false)} className="rounded-lg border-2 border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button onClick={() => setIsTicketCourseFilterModalOpen(false)} className="rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">Apply</button>
                </div>
              </div>
            </div>
          )}

          {selectedTicketCourse && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              onClick={() => setSelectedTicketCourse(null)}
            >
              <div
                className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 z-10 flex items-start justify-between gap-4 rounded-t-2xl bg-gradient-to-r from-violet-50 to-fuchsia-50 px-6 py-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">Category Breakdown</p>
                    <h3 className="mt-0.5 text-lg font-bold text-gray-800">{selectedTicketCourse.courseName}</h3>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {isSuperAdmin && (
                      <button
                        type="button"
                        onClick={downloadCourseCategoryExcel}
                        disabled={ticketCategoryLoading || ticketCategoryDetail.length === 0 || allCourseTicketsDownloading}
                        title="Download every category's ticket detail (student name, ID, batch, etc.) as Excel -- one tab per category"
                        className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
                        {allCourseTicketsDownloading ? 'Preparing…' : 'Download All Categories'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setSelectedTicketCourse(null)}
                      className="rounded-lg p-1.5 text-gray-400 transition hover:bg-white hover:text-gray-700"
                    >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {ticketCategoryLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((k) => (
                        <div key={k} className="h-16 animate-pulse rounded-xl bg-gray-100" />
                      ))}
                    </div>
                  ) : ticketCategoryDetail.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-400">
                      No tickets in any category for this course yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {ticketCategoryDetail.map((cat) => {
                        const barSegments = [
                          { key: 'open', value: cat.open, color: '#f59e0b' },
                          { key: 'inProgress', value: cat.inProgress, color: '#0ea5e9' },
                          { key: 'resolved', value: cat.resolved, color: '#10b981' },
                          { key: 'closed', value: cat.closed, color: '#94a3b8' },
                        ];
                        const isDownloadingThis = categoryDownloadingId === cat.categoryId;
                        return (
                          <div key={cat.categoryId} className="rounded-xl border border-gray-200 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: cat.categoryColor || '#a78bfa' }} />
                                <span className="text-sm font-semibold text-gray-800">{cat.categoryName}</span>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="text-xs font-medium text-gray-400">{cat.total} total</span>
                                {isSuperAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => downloadCategoryTicketsExcel(cat)}
                                    disabled={categoryDownloadingId !== null || cat.total === 0}
                                    title={`Download ${cat.categoryName} tickets (student name, ID, batch, etc.) as Excel`}
                                    className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
                                    {isDownloadingThis ? '…' : 'Excel'}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                              {barSegments.map((seg) => (
                                seg.value > 0 && (
                                  <div
                                    key={seg.key}
                                    style={{ width: `${(seg.value / cat.total) * 100}%`, backgroundColor: seg.color }}
                                  />
                                )
                              ))}
                            </div>

                            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                              <div><p className="text-sm font-bold text-amber-600">{cat.open}</p><p className="text-[10px] text-gray-400">Open</p></div>
                              <div><p className="text-sm font-bold text-sky-600">{cat.inProgress}</p><p className="text-[10px] text-gray-400">In Progress</p></div>
                              <div><p className="text-sm font-bold text-emerald-600">{cat.resolved}</p><p className="text-[10px] text-gray-400">Resolved</p></div>
                              <div><p className="text-sm font-bold text-slate-600">{cat.closed}</p><p className="text-[10px] text-gray-400">Closed</p></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {(distributionLoading || courseOptions.length > 0) && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                {distributionLoading ? (
                  <div className="space-y-4">
                    <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
                    <div className="h-12 w-full animate-pulse rounded-xl bg-gray-100" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Select Course</h3>
                      <p className="mt-1 text-sm text-gray-500">Choose a course to view student distribution</p>
                    </div>
                    <div className="sm:w-96">
                      <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="block w-full rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm">
                        {courseOptions.map((course) => (
                          <option key={course.value} value={course.value}>{course.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {currentCourse && currentCourse.type === 'dual' && batchCountData && batchCountData.length > 0 && selectedCourse === 'hnd' && (
                <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h3 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent">{currentCourse.label}</h3>
                      <p className="mt-2 text-sm text-gray-500">Active students distributed across batches</p>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 px-6 py-3">
                      <div className="text-center">
                        <p className="text-xs font-medium text-gray-600">Total Students</p>
                        <p className="mt-1 text-2xl font-bold text-blue-600">{batchCountData.reduce((sum, d) => sum + ((d.seCount || 0) + (d.neCount || 0)), 0)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4 flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-blue-500"></div><span className="font-medium">Software Engineering</span></div>
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-purple-500"></div><span className="font-medium">Network Engineering</span></div>
                    <div className="ml-auto text-xs"><span className="font-medium">{batchCountData.length}</span> Active Batches</div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={batchCountData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <defs>
                        <linearGradient id="colorSE" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0.7} /></linearGradient>
                        <linearGradient id="colorNE" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.9} /><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.7} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 12, fill: '#6B7280' }} />
                      <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="seCount" name="SE Count" fill="url(#colorSE)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="seCount" position="top" style={{ fontSize: '12px', fill: '#1E40AF', fontWeight: 'bold' }} />
                      </Bar>
                      <Bar dataKey="neCount" name="NE Count" fill="url(#colorNE)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="neCount" position="top" style={{ fontSize: '12px', fill: '#6B21A8', fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {currentCourse && currentCourse.type === 'dual-dt' && selectedCourse === 'hndDT' && hndDigitalTechCountData && hndDigitalTechCountData.length === 0 && !distributionLoading && (
                <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl text-center text-gray-400">
                  <p className="text-sm font-medium">No active batches found for HND Digital Technologies courses.</p>
                  <p className="mt-1 text-xs">Batches will appear here once they are marked as ongoing.</p>
                </div>
              )}

              {currentCourse && currentCourse.type === 'dual-dt' && hndDigitalTechCountData && hndDigitalTechCountData.length > 0 && selectedCourse === 'hndDT' && (
                <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h3 className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-2xl font-bold text-transparent">{currentCourse.label}</h3>
                      <p className="mt-2 text-sm text-gray-500">Active students distributed across batches</p>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 px-6 py-3">
                      <div className="text-center">
                        <p className="text-xs font-medium text-gray-600">Total Students</p>
                        <p className="mt-1 text-2xl font-bold text-emerald-600">{hndDigitalTechCountData.reduce((sum, d) => sum + ((d.daCount || 0) + (d.csCount || 0)), 0)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4 flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-emerald-500"></div><span className="font-medium">Data Analytics</span></div>
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-teal-500"></div><span className="font-medium">Cyber Security</span></div>
                    <div className="ml-auto text-xs"><span className="font-medium">{hndDigitalTechCountData.length}</span> Active Batches</div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={hndDigitalTechCountData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <defs>
                        <linearGradient id="colorDA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.9} /><stop offset="95%" stopColor="#10B981" stopOpacity={0.7} /></linearGradient>
                        <linearGradient id="colorCS" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#14B8A6" stopOpacity={0.9} /><stop offset="95%" stopColor="#14B8A6" stopOpacity={0.7} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 12, fill: '#6B7280' }} />
                      <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                      <Tooltip cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }} />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="daCount" name="DA Count" fill="url(#colorDA)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="daCount" position="top" style={{ fontSize: '12px', fill: '#065F46', fontWeight: 'bold' }} />
                      </Bar>
                      <Bar dataKey="csCount" name="CS Count" fill="url(#colorCS)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="csCount" position="top" style={{ fontSize: '12px', fill: '#134E4A', fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'beng' && bengCountData && bengCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={bengCountData} barColor="#0EA5E9" gradientId="gradBeng" description="Student distribution across software engineering batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'bengCncs' && bengCncsCountData && bengCncsCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={bengCncsCountData} barColor="#10B981" gradientId="gradBengCncs" description="Student distribution across networking and cloud security batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'l3' && l3CountData && l3CountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={l3CountData} barColor="#22C55E" gradientId="gradL3" description="Student distribution across Level 3 diploma batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'cit' && citCountData && citCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={citCountData} barColor="#F97316" gradientId="gradCit" description="Student distribution across IT certificate batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'englishDiploma' && englishDiplomaCountData && englishDiplomaCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={englishDiplomaCountData} barColor="#A855F7" gradientId="gradEnglish" description="Student distribution across English diploma batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'hndBusiness' && hndBusinessCountData && hndBusinessCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={hndBusinessCountData} barColor="#14B8A6" gradientId="gradHndBusiness" description="Student distribution across HND Business batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'baBusiness' && baBusinessAdminCountData && baBusinessAdminCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={baBusinessAdminCountData} barColor="#F59E0B" gradientId="gradBaBusiness" description="Student distribution across business administration batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'l7Sml' && l7SmlCountData && l7SmlCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={l7SmlCountData} barColor="#EF4444" gradientId="gradL7Sml" description="Student distribution across Level 7 SML batches" />
              )}
            </div>
          )}

          {isCourseFilterModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
              <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-emerald-500 p-2 text-white"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg></div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Filter Attendance by Course</h3>
                        <p className="text-sm text-gray-600">Select one or more courses to view attendance data</p>
                      </div>
                    </div>
                    <button onClick={() => setIsCourseFilterModalOpen(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                  </div>
                </div>
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg></div>
                    <input type="text" value={courseSearchQuery} onChange={(e) => setCourseSearchQuery(e.target.value)} placeholder="Search courses by name or schedule type..." className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    {courseSearchQuery && (<button onClick={() => setCourseSearchQuery('')} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>)}
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto px-6 py-4">
                  {attendanceLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((k) => (
                        <div key={k} className="flex items-center gap-3 rounded-lg border-2 border-gray-200 bg-white p-4">
                          <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                            <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
                          </div>
                          <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    (() => {
                      const visibleCourses = filteredAttendanceCourses.filter((c) => {
                        if (!courseSearchQuery) return true;
                        const s = courseSearchQuery.toLowerCase();
                        return (c.fullName || '').toLowerCase().includes(s) || (c.scheduleType || '').toLowerCase().includes(s);
                      });
                      const allVisibleIds = visibleCourses.map((c) => String(c.courseId));
                      const isAllChecked = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedAttendanceCourses.includes(id));
                      const isSomeChecked = !isAllChecked && allVisibleIds.some((id) => selectedAttendanceCourses.includes(id));
                      return (
                        <div className="space-y-2">
                          <label className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 ${isAllChecked ? 'border-emerald-300 bg-emerald-50' : isSomeChecked ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white hover:border-emerald-200'}`}>
                            <input
                              type="checkbox"
                              checked={isAllChecked}
                              ref={(el) => { if (el) el.indeterminate = isSomeChecked; }}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAttendanceCourses((prev) => [...new Set([...prev.filter((x) => x !== 'all'), ...allVisibleIds])]);
                                } else {
                                  setSelectedAttendanceCourses((prev) => prev.filter((id) => !allVisibleIds.includes(id)));
                                }
                              }}
                              className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                            />
                            <div className="flex-1">
                              <span className={`font-semibold ${isAllChecked || isSomeChecked ? 'text-emerald-900' : 'text-gray-900'}`}>
                                {courseSearchQuery ? `Select All Matching (${visibleCourses.length})` : `Select All Courses (${visibleCourses.length})`}
                              </span>
                            </div>
                            {isAllChecked && (
                              <div className="rounded-full bg-emerald-500 p-1"><svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>
                            )}
                          </label>
                          {visibleCourses.map((course) => {
                            const idStr = String(course.courseId);
                            const isSelected = selectedAttendanceCourses.includes(idStr);
                            const hasAttendance = courseAttendanceData.some((c) => String(c.courseId) === idStr);
                            return (
                              <label key={course.courseId} className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 ${isSelected ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white hover:border-emerald-200'}`}>
                                <input type="checkbox" checked={isSelected} onChange={(e) => {
                                  if (e.target.checked) setSelectedAttendanceCourses((prev) => [...prev.filter((x) => x !== 'all'), idStr]);
                                  else setSelectedAttendanceCourses((prev) => prev.filter((x) => x !== idStr));
                                }} className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-semibold ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>{course.fullName}</span>
                                    {course.scheduleType && (
                                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${String(course.scheduleType).toLowerCase() === 'weekday' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{String(course.scheduleType).toLowerCase() === 'weekday' ? '📅 Weekday' : '📆 Weekend'}</span>
                                    )}
                                    {!hasAttendance && (<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">No data</span>)}
                                  </div>
                                  {!hasAttendance && <p className="mt-1 text-xs text-gray-500">No attendance records in the last 30 days</p>}
                                </div>
                                {isSelected && (
                                  <div className="rounded-full bg-emerald-500 p-1"><svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">{selectedAttendanceCourses.length === 0 ? 'Please select at least one course' : selectedAttendanceCourses.includes('all') ? 'Viewing all courses combined' : `Viewing ${selectedAttendanceCourses.length} selected ${selectedAttendanceCourses.length === 1 ? 'course' : 'courses'}`}</p>
                    <div className="flex gap-3">
                      <button onClick={() => setIsCourseFilterModalOpen(false)} className="rounded-lg border-2 border-gray-300 bg-white px-6 py-2 font-semibold text-gray-700">Cancel</button>
                      <button onClick={() => setIsCourseFilterModalOpen(false)} disabled={selectedAttendanceCourses.length === 0} className="rounded-lg bg-emerald-600 px-6 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500">Apply Filter</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add shimmer animation to Tailwind */}
          <style>{`
            @keyframes shimmer {
              100% {
                transform: translateX(100%);
              }
            }
          `}</style>

    </DashboardLayout>
  );
}
